// One room's AC–Fan cycle: what the SERVER says, refreshed while the room is on
// screen, with the countdown ticking locally between refreshes.
//
// ⚠️ The server sends SECONDS LEFT, never a timestamp — the database clock is
// IST labelled UTC, so a parsed time would be 5½ h out. The deadline is this
// phone's clock at the moment the reply arrived + secondsLeft.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getRoomCycle, saveRoomCycle, startRoomCycle, stopRoomCycle, skipRoomCyclePhase,
} from '../api/api';

const POLL_RUNNING_MS = 15000;
const POLL_IDLE_MS = 60000;

export default function useRoomCycle(userId, roomId) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);
  const deadline = useRef(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    if (!userId || !roomId) return;
    try {
      const res = await getRoomCycle(userId, roomId);
      if (!alive.current) return;
      const d = res?.data || null;
      setData(d);
      setError('');
      deadline.current = d && d.secondsLeft != null ? Date.now() + d.secondsLeft * 1000 : null;
    } catch (e) {
      if (!alive.current) return;
      const body = e?.response?.data;
      if (body && body.setup === false) setData({ ...body, setup: false });
      else setError(body?.error || e?.message || 'Could not load');
    }
  }, [userId, roomId]);

  useEffect(() => {
    alive.current = true;
    setData(null);
    load();
    return () => { alive.current = false; };
  }, [load]);

  const running = !!(data && data.state);
  useEffect(() => {
    const t = setInterval(load, running ? POLL_RUNNING_MS : POLL_IDLE_MS);
    return () => clearInterval(t);
  }, [load, running]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const secondsLeft = deadline.current == null ? null : Math.max(0, Math.round((deadline.current - Date.now()) / 1000));

  /** Run an action, then refresh. -> '' on success, else the error text. */
  const act = useCallback(async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
      return '';
    } catch (e) {
      return e?.response?.data?.error || e?.message || 'Failed';
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [load]);

  return {
    data,
    error,
    busy,
    secondsLeft,
    reload: load,
    start: () => act(() => startRoomCycle(userId, roomId)),
    stop: () => act(() => stopRoomCycle(userId, roomId)),
    skip: () => act(() => skipRoomCyclePhase(userId, roomId)),
    save: (settings) => act(() => saveRoomCycle(userId, roomId, settings)),
  };
}
